import React, { forwardRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { AccountManager, SiteConfigManager, useTranslation } from '@atlas/core';
import { ChipsItem, Icon } from '@atlas/ui';

import { useBonusesContext } from 'src/components/BonusesProvider';
import { usePaymentContext } from 'src/components/ClientProfile/Payment/PaymentProvider';
import useFormatter from 'src/hooks/useFormatter';

const MULTIPLIER = 100;

const DepositAmountChip = forwardRef(({ amount, onChipClick }, ref) => {
  const { depositAmount, setDepositAmount } = useBonusesContext();
  const { maxAmount, minAmount } = usePaymentContext();
  const { currencyFormatter } = useFormatter();
  const translation = useTranslation();

  const currency = AccountManager.getAccountCurrency();

  const depositAmountTemplates = SiteConfigManager.getConfig(
    'config.config.accountProfile.depositAmountTemplate',
  );

  const showCurrencyPresets = SiteConfigManager.getConfig(
    'config.config.accountProfile.showCurrencyPresets',
  );

  const showDecimalsPresets = SiteConfigManager.getConfig(
    'config.config.accountProfile.showDecimalsPresets',
  );

  const currentCurrencyConfig = useMemo(() => {
    return depositAmountTemplates?.[currency] || [];
  }, [depositAmountTemplates, currency]);

  const isHighlighted = useMemo(() => {
    return currentCurrencyConfig.some(
      item => Number(item.value) === Number(amount) && item.isHighlighted,
    );
  }, [currentCurrencyConfig, amount]);

  const amountWithMultiplier = amount * MULTIPLIER;

  const isActive = useMemo(
    () => Math.round(amountWithMultiplier) === depositAmount,
    [amountWithMultiplier, depositAmount],
  );

  const handleDepositAmountClick = useCallback(() => {
    let amountValue;

    if (minAmount && amountWithMultiplier < minAmount) {
      amountValue = minAmount;
    } else if (maxAmount && amountWithMultiplier > maxAmount) {
      amountValue = maxAmount;
    } else {
      amountValue = amountWithMultiplier;
    }

    setDepositAmount(amountValue);
    ref.current.setFieldValueHandler('amount', amountValue);
    onChipClick?.(amount);
  }, [amount, amountWithMultiplier, maxAmount, minAmount, onChipClick, ref, setDepositAmount]);

  const formattedAmount = useMemo(() => {
    if (!translation) {
      return '';
    }

    return currencyFormatter.format(amountWithMultiplier, {
      minimumFractionDigits: showDecimalsPresets ? 2 : 0,
      style: showCurrencyPresets ? 'currency' : 'decimal',
    });
  }, [
    amountWithMultiplier,
    showCurrencyPresets,
    showDecimalsPresets,
    translation,
    currencyFormatter,
  ]);

  return (
    <ChipsItem
      active={isActive}
      className="deposit-amounts-chip"
      key={amount}
      label={formattedAmount}
      onClick={handleDepositAmountClick}
    >
      {isHighlighted && <Icon className="highlighted_chip" name="deposit-fire" />}
    </ChipsItem>
  );
});

DepositAmountChip.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChipClick: PropTypes.func,
};

export default DepositAmountChip;
